/**
 * Minimal technician profile — read-only view of the signed-in user
 * (name, role, email) + a language toggle. Edits to staff profiles are a
 * Management/Admin concern; this is the in-bay "who am I logged in as".
 */
import { useState } from 'react'
import { ArrowLeft } from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useSignOut } from '@vollgas/shared/hooks'
import { useCurrentUser, useSession } from '@/auth/SessionContext'
import { useI18n } from '@/i18n/TranslationContext'
import { DetailCard, DetailRow } from '@/features/appointment/DetailCard'
import { ChangePasswordModal } from './ChangePasswordModal'

export function ProfileRoute() {
  const { t, lang, setLang } = useI18n()
  const navigate = useNavigate()
  const user = useCurrentUser()
  const { setUser } = useSession()
  const qc = useQueryClient()
  const [passwordOpen, setPasswordOpen] = useState(false)
  const sessions = useSignOut({
    onSignedOut: () => {
      setUser(null)
      qc.clear()
      navigate('/login', { replace: true })
    },
    messages: {
      failed: t.common.signOutFailed,
      everywhereDone: (n) => t.common.signOutEverywhereDone.replace('{count}', String(n)),
      everywhereNone: t.common.signOutEverywhereNone,
      everywhereFailed: t.common.signOutEverywhereFailed,
    },
  })

  // `displayName` is the backend's resolved name and is never empty; the
  // first/last pair below is a fallback for a stale response only. Joining
  // first + last as the primary source printed the literal "null null" for
  // every seeded staff account — a template literal stringifies null, and
  // "null null" is truthy, so the email fallback never fired.
  const joined = [user?.firstName, user?.lastName].filter((s) => s?.trim()).join(' ')
  const name = user ? user.displayName?.trim() || joined || user.email : '—'

  return (
    <section aria-label={t.profile.title}>
      <button
        type="button"
        onClick={() => navigate('/')}
        className="-ml-1 mb-2 inline-flex items-center gap-1.5 rounded-sm bg-transparent pl-1 pr-2.5 py-1.5 text-13 font-semibold text-glass-text-muted hover:bg-glass-fill-strong hover:text-glass-text"
      >
        <ArrowLeft size={16} weight="bold" />
        {t.common.today}
      </button>

      <h1 className="mb-px18 font-display text-26 font-extrabold tracking-mk-neg5 text-glass-text">{t.profile.title}</h1>

      <DetailCard label={name}>
        {user?.role && <DetailRow label={t.profile.role}>{user.role.key}</DetailRow>}
        {user?.email && <DetailRow label={t.profile.email}>{user.email}</DetailRow>}
        <DetailRow label={t.profile.language}>
          <button
            type="button"
            onClick={() => setLang(lang === 'de' ? 'en' : 'de')}
            className="rounded-full border border-glass-border bg-transparent px-3 py-1 text-12-5 font-semibold text-glass-text-muted hover:bg-glass-fill-strong hover:text-glass-text"
          >
            {lang === 'de' ? 'Deutsch' : 'English'}
          </button>
        </DetailRow>
        {/* Sits with the identity rows because that is what it changes.
            The endpoint is self-service and ungated — a tech reaches it
            exactly as an admin does. */}
        <DetailRow label={t.profile.password}>
          <button
            type="button"
            onClick={() => setPasswordOpen(true)}
            className="rounded-full border border-glass-border bg-transparent px-3 py-1 text-12-5 font-semibold text-glass-text-muted hover:bg-glass-fill-strong hover:text-glass-text"
          >
            {t.profile.changePassword}
          </button>
        </DetailRow>
      </DetailCard>

      {passwordOpen && <ChangePasswordModal onClose={() => setPasswordOpen(false)} />}

      {/* Sign-out lives here, at the bottom of the profile — "everywhere
          else" is a rare, deliberate action and does not belong beside the
          everyday sign-out in the topbar menu. */}
      <DetailCard label={t.common.signOutEverywhere}>
        <div className="flex flex-col gap-3 px-px18 py-px14">
          <p className="text-12-5 text-glass-text-muted">{t.common.signOutEverywhereHint}</p>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              disabled={sessions.busy}
              onClick={() => void sessions.signOut()}
              className="rounded-full bg-accent px-4 py-2 text-12-5 font-semibold text-white transition hover:opacity-90 disabled:opacity-60"
            >
              {t.common.signOut}
            </button>
            <button
              type="button"
              disabled={sessions.busy}
              onClick={() => void sessions.signOutEverywhereElse()}
              className="rounded-full border border-glass-border px-4 py-2 text-12-5 font-semibold text-glass-text transition hover:bg-glass-fill-strong disabled:opacity-60"
            >
              {t.common.signOutEverywhere}
            </button>
          </div>
          {(sessions.error || sessions.notice) ? (
            <p
              role="status"
              className={`text-12-5 font-semibold ${sessions.error ? 'text-red-500' : 'text-glass-text-muted'}`}
            >
              {sessions.error ?? sessions.notice}
            </p>
          ) : null}
        </div>
      </DetailCard>
    </section>
  )
}
